import * as React from 'react';
import {
  LocalizationProvider as BaseLocalizationProvider,
  ReactLocalization as BaseReactLocalization,
} from '@fluent/react';
import { FluentBundle, FluentResource } from '@fluent/bundle';
import { negotiateLanguages } from '@fluent/langneg';
import { paramCase } from 'param-case';

import enUSResource from '#/locales/en-US.ftl?raw';

const DEFAULT_LOCALE = 'en-US';
const RESOURCES = {
  'en-US': new FluentResource(enUSResource),
};
export const AVAILABLE_LOCALES = Object.keys(RESOURCES);

// A generator function responsible for building the sequence
// of FluentBundle instances in the order of user's language
// preferences.
export function* generateBundles(locales: string[]) {
  // Choose locales that are best for the user.
  const currentLocales = negotiateLanguages(locales, AVAILABLE_LOCALES, {
    defaultLocale: DEFAULT_LOCALE,
  });

  for (const locale of currentLocales) {
    const bundle = new FluentBundle(locale);
    bundle.addResource(RESOURCES[locale as keyof typeof RESOURCES]);
    yield bundle;
  }
}

// FIXME: remove this after this issue is fixed https://github.com/projectfluent/fluent.js/issues/411
class ReactLocalization extends BaseReactLocalization {
  reportError(): void {
    // Silence please
  }
}

const LocalesContext = React.createContext<{
  locales: string[];
  setLocales: (locales: string[]) => void;
  // eslint-disable-next-line @typescript-eslint/no-empty-function
}>({ locales: [], setLocales: () => {} });
export const LocalizationProvider: React.FC<LocalizationProviderProps> = ({
  children,
  locales,
}) => {
  const [_locales, setLocales] = React.useState<string[]>(locales);
  const [_l10n, setL10n] = React.useState<ReactLocalization>(new ReactLocalization([]));

  React.useEffect(() => {
    const l10n = new ReactLocalization(generateBundles(_locales));
    setL10n(l10n);
  }, [_locales]);

  return (
    <LocalesContext.Provider value={{ locales: _locales, setLocales }}>
      <BaseLocalizationProvider l10n={_l10n}>{children}</BaseLocalizationProvider>
    </LocalesContext.Provider>
  );
};
type LocalizationProviderProps = {
  children: React.ReactNode;
  locales: string[];
};

export const useLocales = () => {
  const localesContext = React.useContext(LocalesContext);

  return [localesContext.locales, localesContext.setLocales] as [
    string[],
    (locales: string[]) => void,
  ];
};

export function convertPathToTranslatableKey(name: string) {
  const paramCaseName = paramCase(name);
  let key = name;

  if (paramCaseName.includes('-id')) {
    key = paramCaseName.split('-').slice(-2)[0];
  } else if (paramCaseName.includes('price-specification')) {
    key = name
      .split('.')
      .slice(-2)[0]
      .replace(/specification/i, '');
  } else if (paramCaseName.includes('identifier')) {
    key = 'identifier';
  } else {
    key = name.split('.').slice(-1)[0];
  }

  return paramCase(key);
}
